import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import {
  CheckCircle,
  TrendingUp,
  ClipboardList,
  Briefcase,
  Users,
  FileText,
  BarChart2,
  Target,
} from 'lucide-react'

gsap.registerPlugin(ScrollTrigger)
import { SpokeHub } from './SpokeHub'
import { PelajariMascot } from './PelajariMascot'

const avatarImage = '/assets/avatar.png'



// ─── Kinerja Illustration Cards ───────────────────────────────────────────────

function PsychometricScoreCard() {
  return (
    <div className="bg-white border border-[rgba(53,157,158,0.1)] rounded-lg p-3 w-[200px] max-w-full">
      <div className="flex items-center gap-2 mb-2">
        <ClipboardList className="w-4 h-4 text-[#359d9e] flex-shrink-0" />
        <span className="text-xs font-semibold text-[#1a2727]">Big Five Assessment</span>
      </div>
      <div className="mb-2">
        <div className="flex justify-between text-[10px] text-[#b3b7b7] mb-1">
          <span>Completion</span>
          <span>75%</span>
        </div>
        <div className="h-1.5 bg-[#e6e7e7] rounded-full">
          <div className="h-1.5 bg-[#359d9e] rounded-full w-[75%]" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] text-[#b3b7b7]">Score</span>
        <span className="text-xs font-bold text-[#27777a]">82 / 100</span>
      </div>
    </div>
  )
}

function AssessmentSessionCard() {
  return (
    <div className="bg-white border border-[rgba(53,157,158,0.1)] rounded-lg p-3 w-[200px] max-w-full">
      <div className="flex items-center gap-2 mb-2">
        <Target className="w-4 h-4 text-[#fc8213] flex-shrink-0" />
        <span className="text-xs font-semibold text-[#1a2727]">Q1 Performance Review</span>
      </div>
      <div className="flex items-center gap-1.5 mb-1.5">
        <span className="inline-block px-2 py-0.5 rounded-full bg-[#fff7ed] text-[#fc8213] text-[10px] font-semibold">In Progress</span>
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-[10px] text-[#b3b7b7]">Due:</span>
        <span className="text-[10px] text-[#1a2727]">Mar 31, 2026</span>
      </div>
    </div>
  )
}

function KinerjaProfileCard() {
  return (
    <div className="bg-white border border-[rgba(53,157,158,0.1)] rounded-lg p-3 w-[200px] max-w-full">
      <div className="flex flex-col items-center gap-2">
        <div className="w-16 h-16 rounded-full bg-[#fff0e9] overflow-hidden">
          <img src={avatarImage} alt="Jane Doe" className="w-full h-full object-cover" />
        </div>
        <div className="text-center">
          <p className="text-sm font-semibold text-[#1a2727]">Jane Doe</p>
          <p className="text-[11px] text-[#1a2727]">Software Engineer II</p>
        </div>
        <div className="w-full">
          <p className="text-[10px] font-semibold text-[#b3b7b7] mb-1.5 uppercase tracking-wide">Summary</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <BarChart2 className="w-3.5 h-3.5 text-[#359d9e] flex-shrink-0" />
              <span className="text-[11px] text-[#1a2727]">5 Competencies Tracked</span>
            </div>
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-[#359d9e] flex-shrink-0" />
              <span className="text-[11px] text-[#1a2727]">3 Tests Completed</span>
            </div>
            <div className="flex items-center gap-1.5">
              <ClipboardList className="w-3.5 h-3.5 text-[#fc8213] flex-shrink-0" />
              <span className="text-[11px] text-[#1a2727]">Assessment: In Progress</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Wawasan Illustration Cards ───────────────────────────────────────────────

function NineBoxCard() {
  // 3×3 grid — cell [1][2] highlighted (high performance, high potential)
  const cells = [
    ['#f2fbfa', '#d5f2ef', '#359d9e'],
    ['#f2fbfa', '#359d9e', '#27777a'],
    ['#e6e7e7', '#f2fbfa', '#d5f2ef'],
  ]
  return (
    <div className="bg-white border border-[rgba(53,157,158,0.1)] rounded-lg p-3 w-[160px] max-w-full flex-shrink-0">
      <p className="text-xs font-semibold text-[#1a2727] mb-2">9-Box Grid</p>
      <div className="grid grid-cols-3 gap-1">
        {cells.map((row, ri) =>
          row.map((color, ci) => (
            <div
              key={`${ri}-${ci}`}
              className="h-8 rounded flex items-center justify-center text-[10px]"
              style={{ backgroundColor: color }}
            >
              {ri === 1 && ci === 1 && (
                <div className="w-3 h-3 rounded-full bg-white border-2 border-[#27777a]" />
              )}
            </div>
          ))
        )}
      </div>
      <p className="text-[10px] text-[#b3b7b7] mt-2">Jane Doe · High Potential</p>
    </div>
  )
}

function TalentReportCard() {
  return (
    <div className="bg-white border border-[rgba(53,157,158,0.1)] rounded-lg p-3">
      <div className="flex items-center gap-1.5 mb-1">
        <TrendingUp className="w-3.5 h-3.5 text-[#359d9e] flex-shrink-0" />
        <p className="text-xs font-semibold text-[#1a2727]">AI Talent Report</p>
      </div>
      <p className="text-[11px] text-[#1a2727]">High Potential · Low Performance</p>
    </div>
  )
}

function IDPCard() {
  return (
    <div className="bg-white border border-[rgba(53,157,158,0.1)] rounded-lg p-3">
      <p className="text-xs font-semibold text-[#1a2727] mb-1">IDP Goal: Leadership Skills</p>
      <div className="h-1.5 bg-[#e6e7e7] rounded-full">
        <div className="h-1.5 bg-[#359d9e] rounded-full w-[40%]" />
      </div>
      <p className="text-[10px] text-[#b3b7b7] mt-1">40% complete</p>
    </div>
  )
}

// ─── Rekrut Illustration Cards ────────────────────────────────────────────────

function JobPostingIcon() {
  return (
    <div className="bg-white border border-[rgba(53,157,158,0.1)] p-3 rounded-full">
      <Briefcase className="w-6 h-6 text-[#359d9e]" />
    </div>
  )
}

function CandidateIcon() {
  return (
    <div className="bg-white border border-[rgba(53,157,158,0.1)] p-3 rounded-full">
      <Users className="w-6 h-6 text-[#368fc1]" />
    </div>
  )
}

function CVIcon() {
  return (
    <div className="bg-white border border-[rgba(53,157,158,0.1)] p-3 rounded-full">
      <FileText className="w-6 h-6 text-[#fc8213]" />
    </div>
  )
}

function RankedCandidateCard() {
  const candidates = [
    { name: 'Sarah Chen', match: '94%', status: 'Interviewed', color: '#359d9e' },
    { name: 'Budi Santoso', match: '87%', status: 'Screening', color: '#368fc1' },
    { name: 'Rina Putri', match: '79%', status: 'New', color: '#b3b7b7' },
  ]
  return (
    <div className="bg-white border border-[rgba(53,157,158,0.1)] rounded-lg p-3 w-[200px] max-w-full flex-shrink-0">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle className="w-4 h-4 text-[#359d9e]" />
        <span className="text-xs font-semibold text-[#1a2727]">Ranked Candidates</span>
      </div>
      <div className="space-y-2">
        {candidates.map((c) => (
          <div key={c.name} className="flex items-center justify-between gap-2">
            <div>
              <p className="text-[11px] font-semibold text-[#1a2727] leading-tight">{c.name}</p>
              <span
                className="text-[10px] font-medium px-1.5 py-0.5 rounded-full"
                style={{ backgroundColor: `${c.color}18`, color: c.color }}
              >
                {c.status}
              </span>
            </div>
            <span className="text-xs font-bold" style={{ color: c.color }}>{c.match}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ─── Main Section ─────────────────────────────────────────────────────────────

export function LandingIntroduce() {
  const sectionRef = useRef<HTMLElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const card1Ref = useRef<HTMLDivElement>(null)
  const card2Ref = useRef<HTMLDivElement>(null)
  const card3Ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      const revealItem = (el: HTMLElement | null, delay = 0) => {
        if (!el) return
        gsap.set(el, { opacity: 0, y: 28 })
        ScrollTrigger.create({
          trigger: el,
          start: 'top 85%',
          once: true,
          onEnter: () => gsap.to(el, { opacity: 1, y: 0, duration: 0.65, ease: 'power2.out', delay }),
        })
      }
      revealItem(headerRef.current)
      revealItem(card1Ref.current, 0.05)
      revealItem(card2Ref.current, 0.1)
      revealItem(card3Ref.current, 0.15)
    }, sectionRef)

    return () => ctx.revert()
  }, [])

  return (
    <section ref={sectionRef} className="flex flex-col items-center gap-16 px-6 py-16 md:px-10 lg:px-[120px] max-w-screen-xl mx-auto">
      {/* Header */}
      <div ref={headerRef} className="flex flex-col items-center gap-6 max-w-[979px] text-center">
        <span className="inline-flex items-center gap-2 bg-[#f2fbfa] border border-[#359d9e] text-[#359d9e] px-4 py-2 rounded-lg text-sm font-medium sm:text-base">
          <PelajariMascot className="w-5 h-4 flex-shrink-0" />
          Three modules. One platform.
        </span>
        <h2 className="text-[32px] font-semibold leading-[40px] tracking-[-0.5px] text-[#000f0f] sm:text-[40px] sm:leading-[48px] lg:text-[48px] lg:leading-[58px] lg:tracking-[-1px]">
          Everything HR needs, finally connected
        </h2>
        <p className="text-base text-[#1a2727]">
          Your employees aren't just data points — they're individuals who want to grow. Pelajari connects performance management, talent intelligence, and smart recruitment into one platform so your HR team spends less time on manual work and more time on people.
        </p>
      </div>

      {/* Feature Cards Grid */}
      <div className="w-full max-w-[1200px] flex flex-col gap-8">

        {/* Card 1 - Full Width: Kinerja */}
        <div ref={card1Ref} className="bg-white border border-[#d5f2ef] rounded-xl p-8 flex flex-col gap-8 overflow-hidden lg:p-10 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-col gap-4 max-w-[496px]">
            <h3 className="text-[28px] font-semibold leading-[36px] tracking-[-0.5px] text-[#27777a] sm:text-[32px] sm:leading-[40px] lg:text-[36px] lg:leading-[45px] lg:tracking-[-0.75px]">
              Performance, assessments, and growth — unified
            </h3>
            <p className="text-base text-[#1a2727]">
              Track competencies, run psychometric tests across Big Five, DISC, and Leadership frameworks, manage assessment sessions, and give every employee a clear view of their growth journey.
            </p>
            <p className="text-sm text-[#b3b7b7] italic">
              Coming soon: AI-generated IDP drafts, roleplay simulations, and automated learning paths.
            </p>
          </div>

          {/* Illustration A */}
          <div className="w-full lg:max-w-[560px]">
            <SpokeHub
              left={[<PsychometricScoreCard />, <AssessmentSessionCard />]}
              right={[<KinerjaProfileCard />]}
            />
          </div>
        </div>

        {/* Cards Row */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

          {/* Card 2 - Wawasan */}
          <div ref={card2Ref} className="bg-white border border-[#d5f2ef] rounded-xl p-8 overflow-hidden sm:p-10">
            <div className="flex flex-col gap-4 mb-8">
              <h3 className="text-[28px] font-semibold leading-[36px] tracking-[-0.5px] text-[#27777a] sm:text-[32px] sm:leading-[40px] lg:text-[36px] lg:leading-[45px] lg:tracking-[-0.75px]">
                Talent intelligence that drives real decisions
              </h3>
              <p className="text-base text-[#1a2727]">
                Plot your entire team on the 9-Box grid, generate AI-powered talent reports, and build Individual Development Plans grounded in actual competency data.
              </p>
            </div>

            {/* Illustration B */}
            <SpokeHub
              left={[<NineBoxCard />]}
              right={[<TalentReportCard />, <IDPCard />]}
            />
          </div>

          {/* Card 3 - Rekrut */}
          <div ref={card3Ref} className="bg-white border border-[#d5f2ef] rounded-xl p-8 overflow-hidden sm:p-10">
            <div className="flex flex-col gap-4 mb-8">
              <h3 className="text-[28px] font-semibold leading-[36px] tracking-[-0.5px] text-[#27777a] sm:text-[32px] sm:leading-[40px] lg:text-[36px] lg:leading-[45px] lg:tracking-[-0.75px]">
                Hire the right people, faster
              </h3>
              <p className="text-base text-[#1a2727]">
                Post open roles, upload candidate CVs for AI competency extraction, and get automatically ranked shortlists so your team focuses on the best fits.
              </p>
            </div>

            {/* Illustration C */}
            <SpokeHub
              left={[<JobPostingIcon />, <CandidateIcon />, <CVIcon />]}
              right={[<RankedCandidateCard />]}
            />
          </div>

        </div>
      </div>
    </section>
  )
}
